import React, {useEffect, useReducer, useState, useRef} from "react";
import ClassStylesEditor from "./ClassStylesEditor";
import BaseStyles from "./Styles/BaseStyles";
import CustomBlockStyles from "./Styles/CustomBlockStyles";
import SimpleBlockStyles from "./Styles/SimpleBlockStyles";
import axios from "../../common/axios";
import ButtonsGroup from "../../common/ButtonsGroup";
import Button from "../../common/Button";
import {ExampleBlockBody} from "../../lesson/lessonContentStyleGuide/LessonContentStyleGuide";
import BasicTableStyles from "./Styles/BasicTableStyles";

export const EMPTY_CONT_STYLE_PROP = 'empty';

const classComponents = {
    ['cont-base']: {component: BaseStyles},
    ['cont-block-custom-*']: {totalVariants: 10, component: CustomBlockStyles},
    ['cont-block-simple-*']: {totalVariants: 10, component: SimpleBlockStyles},
    ['cont-table-basic']: {totalVariants: 1, component: BasicTableStyles}
}
const hoverClass = 'cont-editor-hover';
const INDENT = '    ';

const contentStylesEditor = props => {
    const ACTIONS = {
        HYDRATE_STATE: 'hydrateState',
        EDIT_CLASS: 'editClass',
        UPDATE_CLASS_STYLES: 'updateClassStyles',
        UPDATE_UNSAVED_CHANGES: 'updateUnsavedChanges'
    }
    const reducer = (state, action) => {
        if (action.type === ACTIONS.HYDRATE_STATE) {
            return {...state, styles: action.payload.styles, editableClasses: action.payload.editableClasses}
        } else if (action.type === ACTIONS.EDIT_CLASS) {
            return {...state, edit: {class: action.payload.class}}
        } else if (action.type === ACTIONS.UPDATE_CLASS_STYLES) {
            const updatedClassNewStyles = {
                ...(state.styles[action.payload.className] || {}),
                ...action.payload.newStyles
            }
            const filteredUpdatedClassNewStyles = Object.keys(updatedClassNewStyles).reduce((acc, style) => {
                if (updatedClassNewStyles[style] !== EMPTY_CONT_STYLE_PROP) {
                    acc[style] = updatedClassNewStyles[style];
                }
                return acc;
            }, {});
            return {
                ...state,
                styles: {
                    ...state.styles,
                    [action.payload.className]: filteredUpdatedClassNewStyles
                }
            };
        } else if (action.type === ACTIONS.UPDATE_UNSAVED_CHANGES) {
            return {...state, unsavedChanges: action.payload.newChanges}
        } else {
            return state;
        }
    }
    const defaultState = {
        styles: {},
        editableClasses: {},
        edit: {},
        unsavedChanges: false
    }
    const [state, dispatch] = useReducer(reducer, defaultState);
    const [defaultStyleCSS, setDefaultStyleCSS] = useState(null);
    const [editStyleCSS, setEditStyleCSS] = useState(null);
    const workspaceRef = useRef();
    const editableClassesRef = useRef(state.editableClasses);

    useEffect(() => {
        const contentStyleSheet = document.getElementById('syllabusContentStyleSheet');
        if (contentStyleSheet) contentStyleSheet.disabled = true;

        initLoad();

        workspaceRef.current.addEventListener('mouseover', addHoverEffectListener);
        workspaceRef.current.addEventListener('mouseout', removeHoverEffectListener);
        workspaceRef.current.addEventListener('click', clickListener);
        return () => {
            if (contentStyleSheet) contentStyleSheet.removeAttribute('disabled');
            updateEditClass(null);
            if (workspaceRef.current) {
                workspaceRef.current.removeEventListener('mouseover', addHoverEffectListener);
                workspaceRef.current.removeEventListener('mouseout', removeHoverEffectListener);
                workspaceRef.current.removeEventListener('click', clickListener);
            }
        }
    }, []);

    useEffect(() => {
        if (state.styles) setEditStyleCSS(convertToCSS(state.styles));
    }, [state.styles]);

    useEffect(() => {
        editableClassesRef.current = state.editableClasses;
    }, [state.editableClasses]);

    useEffect(() => {
        console.log('state', state);
    }, [state]);

    const initLoad = () => {
        const newEditableClasses = buildEditableClasses();
        buildDefaultStyleCSS(newEditableClasses);
        axios.get('/web/ui/contentStyles/getStyles')
            .then(response => {
                const newStyles = JSON.parse(response.data);
                dispatch({type: ACTIONS.HYDRATE_STATE, payload: {styles: newStyles, editableClasses: newEditableClasses}});
            });
    }

    const buildEditableClasses = () => {
        let newEditableClasses = {};
        for (const [baseClass, {totalVariants, component}] of Object.entries(classComponents)) {
            if (totalVariants) {
                for (let i = 1; i <= totalVariants; i++) {
                    newEditableClasses[baseClass.replace('*', i)] = {component: component, variant: i}
                }
            } else {
                newEditableClasses[baseClass] = {component: component}
            }
        }
        return newEditableClasses;
    }

    const buildDefaultStyleCSS = (editableClasses) => {
        let cssString = `.cont-base * {\n${INDENT}pointer-events: none;\n}\n`;
        Object.entries(editableClasses).map(([className, {component, variant}], index) => {
            cssString += `${index !== 0 ? ',' : ''}.cont-base .${className}`;
        });
        cssString += ` {\n${INDENT}pointer-events: auto;\n}\n`;
        cssString +=
            `.${hoverClass} {
                cursor: pointer;
                position: relative;
                --edit-colour-RGB: 24, 144, 255;
            }
            .${hoverClass}::before {
                content: "";
                position: absolute;
                top: -4px;
                left: -4px;
                right: -4px;
                bottom: -4px;
                border: 2px solid rgb(var(--edit-colour-RGB));
                pointer-events: none;
                z-index: 999;
            }
            .${hoverClass}::after {
                content: attr(data-name);
                position: absolute;
                top: -4px;
                right: -4px;
                background: rgb(var(--edit-colour-RGB));
                color: #fff;
                font-size: 12px;
                padding: var(--small-spacing-unit) var(--spacing-unit);
                pointer-events: none;
                z-index: 1000;
            }`;
        setDefaultStyleCSS(cssString);
    }

    const addHoverEffectListener = event => {
        const targetElement = event.target;
        const classList = Array.from(targetElement.classList);
        for (const className of classList) {
            const componentData = editableClassesRef.current[className];
            if (componentData) {
                targetElement.classList.add(hoverClass);
                targetElement.setAttribute('data-name', componentData.name || className);
            }
        }
    }

    const removeHoverEffectListener = () => {
        const highlightedElements = workspaceRef.current.getElementsByClassName(hoverClass);
        Array.from(highlightedElements).forEach(el => {
            el.classList.remove(hoverClass);
            el.removeAttribute('data-name');
        });
    }

    const clickListener = event => {
        let targetElement = event.target;
        while (targetElement && workspaceRef.current.contains(targetElement)) {
            const classList = Array.from(targetElement.classList);
            for (const className of classList) {
                const componentData = editableClassesRef.current[className];
                if (componentData) {
                    updateEditClass(className);
                    return;
                }
            }
            targetElement = targetElement.parentNode;
        }
    }

    const updateEditClass = (className) => {
        dispatch({type: ACTIONS.EDIT_CLASS, payload: {class: className}});
    }

    const updateStyles = (className, newStyles) => {
        dispatch({type: ACTIONS.UPDATE_CLASS_STYLES, payload: {className: className, newStyles: newStyles}});
        dispatch({type: ACTIONS.UPDATE_UNSAVED_CHANGES, payload: {newChanges: true}});
    }

    const saveStyles = () => {
        axios.post('/web/ui/contentStyles/updateStyles', JSON.stringify(state.styles), {headers: {'Content-Type': 'application/json'}})
            .then(response => {
                if (response.data.success) {
                    displayToastMessage('Saved', 'The styles have been saved', 'success');
                    dispatch({type: ACTIONS.UPDATE_UNSAVED_CHANGES, payload: {newChanges: false}});
                } else {
                    let errorMessage = 'An error occurred during the update';
                    if (response.data.message) errorMessage = response.data.message;
                    displayToastMessage('Error', errorMessage, 'error');
                }
            });
    }

    const publishStyles = () => {
        axios.get('/web/ui/contentStyles/publishStyles')
            .then(response => {
                if (response.data.success) {
                    displayToastMessage('Published', 'The styles have been published', 'success');
                } else {
                    let errorMessage = 'An error occurred, preventing the styles from being published';
                    if (response.data.message) errorMessage = response.data.message;
                    displayToastMessage('Error', errorMessage, 'error');
                }
            });
    }

    const downloadStyles = () => {
        axios.get('/web/ui/contentStyles/downloadStyles', {}, {responseType: 'blob'})
            .then(response => {
                const contentDisposition = response.headers['content-disposition'];
                const filename = contentDisposition ? contentDisposition.split('filename=')[1].replace(/"/g, '') : 'contentStyles.css';
                const fileURL = window.URL.createObjectURL(new Blob([response.data], { type: 'text/css' }));
                const fileLink = document.createElement('a');
                fileLink.href = fileURL;
                fileLink.setAttribute('download', filename);
                document.body.appendChild(fileLink);
                fileLink.click();

                document.body.removeChild(fileLink);
            })
            .catch(error => {
                console.error('Error downloading the file', error);
            });
    }

    const convertToCSS = stylesObject => {
        let cssString = '';
        Object.entries(stylesObject).forEach(([className, styles]) => {
            cssString += `${className === 'cont-base' ? '' : '.cont-base '}.${className} {\n`
            Object.entries(styles).forEach(([style, value]) => {
                cssString += `${INDENT}${style}: ${value};\n`;
            });
            cssString += `}\n`;
        });
        return cssString;
    }

    return (
        <>
            <style>
                {defaultStyleCSS}
                {editStyleCSS}
            </style>
            <ClassStylesEditor
                display={state.edit.class != null}
                editableClasses={editableClassesRef.current}
                className={state.edit.class}
                styles={state.styles[state.edit.class]}
                updateEditClass={(newClass) => updateEditClass(newClass)}
                updateStylesCallback={(newStyles) => updateStyles(state.edit.class, newStyles)}
            />
            <ButtonsGroup className="margin-t--l">
                <Button type={state.unsavedChanges ? "outline" : ""} onClick={saveStyles}>Save</Button>
                <Button onClick={publishStyles}>Publish</Button>
                <Button onClick={downloadStyles}>Download</Button>
            </ButtonsGroup>
            <div ref={workspaceRef} className="margin-t--l">
                <div className='cont-base' style={{position: 'relative'}}>
                    <ExampleContent />
                </div>
            </div>
        </>
    )
}

const ExampleContent = props => {
    return (
        <>
            <span className="cont-head-c">Emphasis text</span>
            <p className="cont-emph">Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod
                tempor incididunt ut labore et dolore magna aliqua.</p>
            <span className="cont-head-c">Emphasis colour text</span>
            <p className="cont-emph-colour">Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do
                eiusmod tempor incididunt ut labore et dolore magna aliqua.</p>
            <span className="cont-head-c">Coloured text</span>
            <p className="cont-colour-main">Coloured text, main colour</p>
            <p className="cont-colour-custom-1">Coloured text, custom colour 1</p>
            <p className="cont-colour-custom-2">Coloured text, custom colour 2</p>
            <p className="cont-colour-custom-3">Coloured text, custom colour 3</p>
            <p className="cont-colour-custom-4">Coloured text, custom colour 4</p>
            <p className="cont-colour-custom-5">Coloured text, custom colour 5</p>
            <p className="cont-colour-custom-6">Coloured text, custom colour 6</p>
            <p className="cont-colour-custom-7">Coloured text, custom colour 7</p>
            <p className="cont-colour-custom-8">Coloured text, custom colour 8</p>
            <p className="cont-colour-custom-9">Coloured text, custom colour 9</p>
            <p className="cont-colour-custom-10">Coloured text, custom colour 10</p>
            <span className="cont-head-c">Coloured background text</span>
            <p className="cont-colour-bg-main">Coloured background text, main colour</p>
            <p className="cont-colour-bg-custom-1">Coloured background text, custom colour 1</p>
            <p className="cont-colour-bg-custom-2">Coloured background text, custom colour 2</p>
            <p className="cont-colour-bg-custom-3">Coloured background text, custom colour 3</p>
            <p className="cont-colour-bg-custom-4">Coloured background text, custom colour 4</p>
            <p className="cont-colour-bg-custom-5">Coloured background text, custom colour 5</p>
            <p className="cont-colour-bg-custom-6">Coloured background text, custom colour 6</p>
            <p className="cont-colour-bg-custom-7">Coloured background text, custom colour 7</p>
            <p className="cont-colour-bg-custom-8">Coloured background text, custom colour 8</p>
            <p className="cont-colour-bg-custom-9">Coloured background text, custom colour 9</p>
            <p className="cont-colour-bg-custom-10">Coloured background text, custom colour 10</p>
            <span className="cont-head-c">Table</span>
            <table className={'cont-table cont-table-basic'}>
                <thead>
                <tr>
                    <th scope='col'>First Name</th>
                    <th scope='col'>Last Name</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                    <td>Bart</td>
                    <td>Simpson</td>
                </tr>
                </tbody>
            </table>
            <span className="cont-head-c">Custom blocks</span>
            {
                (() => {
                    const customBlock = classComponents['cont-block-custom-*'];
                    let customBlocksJSX = [];
                    for (let i = 1; i <= customBlock.totalVariants; i++) {
                        customBlocksJSX.push(
                            <div key={i} className={`cont-block cont-block-custom-${i}`}>
                                <div className='cont-block-head'>
                                    Custom block {i}
                                </div>
                                <div className='cont-block-body'>
                                    <ExampleBlockBody/>
                                </div>
                            </div>
                        );
                    }
                    return customBlocksJSX;
                })()
            }
            <span className="cont-head-c">Simple blocks</span>
            {
                (() => {
                    const simpleBlock = classComponents['cont-block-simple-*'];
                    let simpleBlocksJSX = [];
                    for (let i = 1; i <= simpleBlock.totalVariants; i++) {
                        simpleBlocksJSX.push(
                            <div key={i} className={`cont-block cont-block-simple-${i}`}>
                                <div className='cont-block-head'>
                                    Simple block {i}
                                </div>
                                <div className='cont-block-body'>
                                    Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                                </div>
                            </div>
                        );
                    }
                    return simpleBlocksJSX;
                })()
            }
        </>
    )
}

export default contentStylesEditor;